import { Machine, get_preferred_connection, OfflineMachine, LocalHostConnection, RemoteConnection, InsecureConnection, ProxyConnection, IConnection } from './Connections';
import { versionCompare } from './Common';

export interface RefreshResult {
    onlineMachines: Machine[];
    localMachines: Machine[];
    offlineMachines: OfflineMachine[];
}

const MY_HOST = import.meta.env.VITE_MY_HOST || 'https://spectre-licensing.com';
export const ONLINE_MACHINES_KEY = 'onlineMachines';
export const LOCAL_MACHINES_KEY = 'localMachines';
const UPDATE_REQUIRED = import.meta.env.VITE_UPDATE_REQUIRED;

// Function to check if a machine version is outdated
export function isMachineOutdated(machineVersion: string): boolean {
    if (!UPDATE_REQUIRED || !machineVersion) return false;
    // versionCompare returns 1 if v1 > v2, 0 if equal, -1 if v1 < v2
    // We consider the machine outdated if machineVersion < UPDATE_REQUIRED
    return versionCompare(machineVersion, UPDATE_REQUIRED, {}) === -1;
};

// Function to recreate connection from cached connection type
export function recreateConnectionFromType(connectionType: string, machineData: any, cookies: string): IConnection {
    switch (connectionType) {
        case 'LocalHost':
            return new LocalHostConnection(machineData);
        case 'Remote':
            return new RemoteConnection(machineData);
        case 'Insecure':
            return new InsecureConnection(machineData);
        case 'Proxy':
            return new ProxyConnection(machineData, cookies);
        default:
            throw new Error(`Unknown connection type: ${connectionType}`);
    }
}

export async function refreshMatchmakingData(cookiesUser64: string, signal?: AbortSignal): Promise<RefreshResult> {
    // Fetch match list
    const response = await fetch(`${MY_HOST}/api/matchmaking/list`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
        signal,
    });
    if (!response.ok) throw new Error(`Matchmaking list failed: ${response.status}`);

    const data = await response.json();
    if (!data || !data.matches) {
        // Clear caches and return empty
        localStorage.removeItem(ONLINE_MACHINES_KEY);
        localStorage.removeItem(LOCAL_MACHINES_KEY);
        return { onlineMachines: [], localMachines: [], offlineMachines: [] };
    }

    // Cache raw match objects by machine name like existing flow
    data.matches.forEach((machine: Machine) => localStorage.setItem(machine.machineName, JSON.stringify(machine)));

    const results = await Promise.all(
        data.matches.map(async (m: Machine) => {
            try {
                const conn = await get_preferred_connection(m, cookiesUser64, 3, 500);
                return { m, conn } as const;
            } catch {
                return { m, conn: null } as const;
            }
        })
    );

    const nextOnline: Machine[] = [];
    const nextLocal: Machine[] = [];
    const nextOffline: OfflineMachine[] = [];

    results.forEach(({ m, conn }) => {
        if (!conn) {
            const offlineMachine = new OfflineMachine(
                m.machineName,
                m.localIP,
                m.publicIP,
                m.matchmakingID,
                m.location,
                m.macAddress,
                m.scanMode,
                m.version,
                m.serialNumber,
                m.isOutdated = isMachineOutdated(m.version)
            );
            
            // Add isOutdated property to the offline machine object
            offlineMachine.isOutdated = isMachineOutdated(m.version);
            
            nextOffline.push(offlineMachine);
            return;
        }
        const machineObj = new Machine(
            conn,
            m.machineName,
            m.localIP,
            m.publicIP,
            m.matchmakingID,
            m.location,
            m.macAddress,
            m.scanMode,
            m.certification,
            m.version,
            m.serialNumber,
            m.isOutdated = isMachineOutdated(m.version)
        );
        
        if (conn instanceof LocalHostConnection) nextLocal.push(machineObj);
        nextOnline.push(machineObj);
    });

    // Save to localStorage for reuse
    localStorage.setItem(
        ONLINE_MACHINES_KEY,
        JSON.stringify(
            nextOnline.map((m) => ({
                machineName: m.machineName,
                localIP: m.localIP,
                publicIP: m.publicIP,
                matchmakingID: m.matchmakingID,
                location: m.location,
                macAddress: m.macAddress,
                scanMode: m.scanMode,
                certification: m.certification,
                version: m.version,
                serialNumber: m.serialNumber,
                connectionType: (m as Machine).connection.type(),
                isOutdated: m.isOutdated
            }))
        )
    );
    localStorage.setItem(
        LOCAL_MACHINES_KEY,
        JSON.stringify(
            nextLocal.map((m) => ({
                machineName: m.machineName,
                localIP: m.localIP,
                publicIP: m.publicIP,
                matchmakingID: m.matchmakingID,
                location: m.location,
                macAddress: m.macAddress,
                scanMode: m.scanMode,
                certification: m.certification,
                version: m.version,
                serialNumber: m.serialNumber,
                connectionType: (m as Machine).connection.type(),
                isOutdated: m.isOutdated
            }))
        )
    );

    return { onlineMachines: nextOnline, localMachines: nextLocal, offlineMachines: nextOffline };
}
